import React, { forwardRef, useImperativeHandle, useState } from 'react'
import { Box, useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import {
  DocumentElement,
  DocumentElementType,
  Field,
  FieldProp,
  Nil,
  RichTextDocumentElement,
  SignatureDocumentElementFieldValue,
} from '../../types'
import {
  createDocumentElementField,
  getInputDocumentElements,
} from '../../utils/consentFormUtils'
import useFields from '../../utils/validation'
import Calendar from '../inputs/calendar/Calendar'
import { DateTimeCalendar } from '../inputs/dateTimeCalendar/DateTimeCalendar'
import PuiCheckbox from '../inputs/PuiCheckbox'
import PuiInput from '../inputs/PuiInput'
import PuiTextField from '../inputs/PuiTextField'
import SignatureDialog from '../signature/SignatureDialog'
import SignatureView from '../signature/SignatureView'
import Text from '../Text'
import { ConsentFormMultiSelect } from './ConsentFormMultiSelect'
import { ConsentFormRichText } from './ConsentFormRichText'
import { ConsentFormSingleSelect } from './ConsentFormSingleSelect'

const useStyles = makeStyles(
  () => ({
    singleCheckbox: {
      paddingTop: 0,
    },
    singleCheckboxLabel: {
      display: 'flex',
      alignItems: 'flex-start',
    },
  }),
  { name: 'ConsentFormPage' },
)

interface ConsentFormPageProps {
  readonly documentElements: DocumentElement[]
  readonly onFieldChange?: (
    elementIndex: number,
    value:
    | string
    | string[]
    | boolean
    | SignatureDocumentElementFieldValue
    | null,
  ) => void
  readonly readOnly?: boolean
}

export interface ConsentFormPageHandle {
  validate: () => boolean
}

export const ConsentFormPage = forwardRef<
ConsentFormPageHandle,
ConsentFormPageProps
>(function ConsentFormPage(
  { documentElements, onFieldChange, readOnly = false },
  ref,
) {
  const theme = useTheme()
  const classes = useStyles()
  const [editingSignature, setEditingSignature] = useState(false)

  const elementsForFields = getInputDocumentElements(documentElements)
  const { fields, validate } = useFields(
    elementsForFields
      .map(element => createDocumentElementField(element))
      // Assure the compiler that filtering out null makes the type not null
      .filter(cur => !R.isNil(cur)) as FieldProp[],
    false,
  )

  useImperativeHandle(ref, () => ({
    validate,
  }))

  return (
    <>
      {documentElements
        .map((documentElement, i) => ({ documentElement, elementIndex: i }))
        .filter(
          ({ documentElement }) =>
            documentElement.type !== DocumentElementType.PAGE_BREAK,
        )
        .map(({ documentElement, elementIndex }) => {
          switch (documentElement.type) {
            case DocumentElementType.TEXT:
              return (
                <ConsentFormRichText
                  key={documentElement.id}
                  value={(documentElement as RichTextDocumentElement).content}
                />
              )
            case DocumentElementType.TEXT_FIELD: {
              const field = fields[documentElement.label]

              return (
                <PuiTextField
                  disabled={readOnly}
                  field={field}
                  key={documentElement.id}
                  label={documentElement.label}
                  onChange={e => {
                    field.setValue(e.target.value)
                    onFieldChange?.(elementIndex, e.target.value)
                  }}
                />
              )
            }
            case DocumentElementType.DATE_TIME: {
              const field = fields[documentElement.label]

              return (
                <Box key={documentElement.id}>
                  <DateTimeCalendar
                    disabled={readOnly}
                    field={fields[documentElement.label]}
                    label={documentElement.label}
                    onChange={(value: string | Nil) => {
                      field.setValue(value)
                      onFieldChange?.(elementIndex, value ?? null)
                    }}
                  />
                </Box>
              )
            }
            case DocumentElementType.DATE: {
              const field = fields[documentElement.label]

              return (
                <Box key={documentElement.id}>
                  <Calendar
                    preferOnChangeProp
                    disabled={readOnly}
                    field={fields[documentElement.label]}
                    label={documentElement.label}
                    onChange={(value: string | Nil) => {
                      field.setValue(value)
                      onFieldChange?.(elementIndex, value ?? null)
                    }}
                  />
                </Box>
              )
            }
            case DocumentElementType.SINGLE_CHECKBOX: {
              const index = documentElement.label || documentElement.id
              return (
                <PuiInput field={fields[index]} key={documentElement.id}>
                  <Box>
                    <Text>{documentElement.label}</Text>
                    <Box
                      sx={{
                        padding: `${theme.spacing(1)} 0`,
                      }}
                    >
                      <PuiCheckbox
                        preferOnChangeProp
                        checkboxClasses={{
                          root: classes.singleCheckbox,
                        }}
                        classes={{ labelRoot: classes.singleCheckboxLabel }}
                        disabled={readOnly}
                        field={fields[index]}
                        label={documentElement.option.name}
                        onChange={e => {
                          onFieldChange?.(elementIndex, e.target.checked)
                        }}
                      />
                    </Box>
                  </Box>
                </PuiInput>
              )
            }
            case DocumentElementType.SINGLE_SELECT:
              return (
                <PuiInput
                  field={fields[documentElement.label]}
                  key={documentElement.id}
                >
                  <Box
                    sx={{
                      marginRight: 'auto',
                    }}
                  >
                    <ConsentFormSingleSelect
                      disabled={readOnly}
                      field={fields[documentElement.label]}
                      label={documentElement.label}
                      options={R.sortBy(R.prop('sequenceNumber'))(
                        documentElement.options,
                      )}
                      onChange={value => {
                        onFieldChange?.(elementIndex, value)
                      }}
                    />
                  </Box>
                </PuiInput>
              )
            case DocumentElementType.MULTI_SELECT: {
              const field = fields[documentElement.label]
              return (
                <PuiInput
                  field={
                    {
                      open: field.open,
                      valid: field.valid,
                      message: field.message,
                    } as Field
                  }
                  key={documentElement.id}
                >
                  <Box key={documentElement.id}>
                    <ConsentFormMultiSelect
                      disabled={readOnly}
                      field={fields[documentElement.label]}
                      items={R.sortBy(R.prop('sequenceNumber'))(
                        documentElement.options,
                      )}
                      key={documentElement.id}
                      label={documentElement.label}
                      onChange={value => {
                        onFieldChange?.(elementIndex, value)
                      }}
                    />
                  </Box>
                </PuiInput>
              )
            }

            case DocumentElementType.SIGNATURE: {
              const field = fields[documentElement.label || documentElement.id]
              const value = field.value as SignatureDocumentElementFieldValue

              return (
                <>
                  <PuiInput field={field} key={documentElement.id}>
                    <Box>
                      <Text
                        sx={{
                          marginBottom: theme.spacing(2),
                        }}
                      >
                        {documentElement.label}
                      </Text>
                      <SignatureView
                        disabled={readOnly}
                        signatureDate=""
                        signatureString={value?.signature}
                        onEdit={() => {
                          setEditingSignature(true)
                        }}
                      />
                    </Box>
                  </PuiInput>
                  <SignatureDialog
                    open={editingSignature}
                    outputFormat="svg"
                    onClose={() => setEditingSignature(false)}
                    onSign={(signer, signature) => {
                      const newValue = {
                        signature,
                        signer,
                      }

                      field.setValue(newValue)
                      onFieldChange?.(elementIndex, newValue)
                    }}
                  />
                </>
              )
            }
            default:
              throw new Error(
                'Attempted to display an invalid document element in a consent form',
              )
          }
        })}
    </>
  )
})
